import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { GALLERY, GALLERY_INTRO } from "@/data/content";
import { useT } from "@/i18n/LanguageContext";

export const Gallery = () => {
  const t = useT();
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-24 lg:py-32 wf-mesh">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <span className="uppercase text-xs tracking-[0.25em] text-[#1A1A1F] font-bold">
            {t(GALLERY_INTRO.eyebrow)}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-4">
            {t(GALLERY_INTRO.title)}
          </h2>
          <p className="text-[#62646B] leading-relaxed mt-6">{t(GALLERY_INTRO.description)}</p>
        </motion.div>

        <div
          data-testid="gallery-grid"
          className="columns-1 sm:columns-2 lg:columns-3 gap-5 mt-14 [&>*]:mb-5"
        >
          {GALLERY.map((img, i) => (
            <motion.button
              key={img.src}
              type="button"
              onClick={() => setActive(img)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              data-testid={`gallery-item-${i}`}
              className="group relative block w-full overflow-hidden rounded-2xl border border-[#E8E7E2] hover:border-[#0A0A0F]/30 transition-colors duration-300 break-inside-avoid"
            >
              <img
                src={img.src}
                alt={t(img.caption)}
                loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#FAFAF7]/90 to-transparent px-5 pt-12 pb-4 text-left">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-[#0A0A0F] font-bold">
                  {t(img.category)}
                </span>
                <span className="block text-sm text-[#0A0A0F] mt-1">{t(img.caption)}</span>
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      {active && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-[#FAFAF7]/95 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setActive(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 w-11 h-11 rounded-full border border-[#E8E7E2] flex items-center justify-center text-[#0A0A0F] hover:border-[#0A0A0F] transition-colors duration-300"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <figure className="max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={active.src}
              alt={t(active.caption)}
              className="max-h-[78vh] w-auto mx-auto rounded-2xl"
            />
            <figcaption className="text-center text-sm text-[#62646B] mt-4">
              {t(active.caption)} — {t(active.category)}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
};
